import React from 'react';
import { Form, Row, Col, Button, Select, message, AutoComplete, Switch, Modal, Checkbox } from 'antd';
import { bulkUpdatePharmacy, previewBulkUpdateCases } from '../../../api/StatesAPI';
import { usePharmacies } from '../../../hooks/usePharmacies';
import { BulkUpdateForm, CheckboxAddon, InlineSwitchItem, SelectWithAddon, SwitchLabelRow } from './StyledComponents';

const CONSULTATION_TYPES = [
  { key: 'store-and-forward', label: 'STORE AND FORWARD', value: 'store and forward' },
  { key: 'audio', label: 'AUDIO', value: 'audio' },
  { key: 'video', label: 'VIDEO', value: 'video' }
];

const makeSelectAllHandlers = (form, fieldName, allIds, setSel) => ({
  onToggle: (checked) => {
    setSel(checked);
    form.setFieldsValue({ [fieldName]: checked ? allIds : [] });
  },
  onChange: (selected = []) => {
    setSel(!!allIds.length && selected.length === allIds.length);
  },
});

export const BulkUpdate = ({ states, fetchStates, categories }) => {
  const [form] = Form.useForm();
  const {
    pharmacySearchResults,
    pharmaciesLoading,
    selectPharmacyByNcpdpId,
    selectedPharmacy,
    searchPharmacies
  } = usePharmacies();
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [selectAllStates, setSelectAllStates] = React.useState(false);
  const [selectAllCategories, setSelectAllCategories] = React.useState(false);

  const allStateIds = React.useMemo(() => states.map(s => s._id), [states]);
  const allCategoryIds = React.useMemo(() => categories.map(c => c._id), [categories]);
  const statesOptions = React.useMemo(() => states.map(s => ({ label: s.name, value: s._id })), [states]);
  const categoriesOptions = React.useMemo(() => categories.map(c => ({ label: c.name, value: c._id })), [categories]);
  const pharmacyOptions = React.useMemo(
    () => pharmacySearchResults?.map(({ ncpdpId, name, city, state }) => ({
      label: `${name}, ${city} ${state}`,
      value: name,
      ncpdpId
    })),
    [pharmacySearchResults]
  );

  const statesSelectAll = React.useMemo(
    () => makeSelectAllHandlers(form, 'states', allStateIds, setSelectAllStates),
    [form, allStateIds]
  );
  const categoriesSelectAll = React.useMemo(
    () => makeSelectAllHandlers(form, 'categories', allCategoryIds, setSelectAllCategories),
    [form, allCategoryIds]
  );

  const handleBulkUpdate = ({ states, categories, consultationType, renewalConsultationType, shouldUpdateCases }) => {
    if (!selectedPharmacy) {
      return message.error('Please select a pharmacy from the list');
    }

    const pharmacyPayload = {
      id: selectedPharmacy.pharmacyId,
      name: selectedPharmacy.name
    };

    bulkUpdatePharmacy(
      states,
      categories,
      pharmacyPayload,
      consultationType,
      renewalConsultationType,
      shouldUpdateCases
    )
      .then(fetchStates)
      .then(() => {
        message.success('Successfully updated the configured pharmacy for the selected states and categories');
        form.resetFields();
        setSelectAllStates(false);
        setSelectAllCategories(false);
      })
      .catch(() => message.error('Failed to update the configured pharmacy for the selected states and categories'));
  };

  return (
    <BulkUpdateForm form={form} layout='vertical' onFinish={handleBulkUpdate}>
      <Row>
        <Col span={24}>
          <SelectWithAddon>
            <Form.Item
              name='states'
              label='States:'
              rules={[{ required: true, message: 'Please select at least one state' }]}
            >
              <Select
                id="state-bulk-update-states-select"
                mode='multiple'
                showSearch
                allowClear
                filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                options={statesOptions}
                placeholder='Enter State Name'
                onChange={statesSelectAll.onChange}
              />
            </Form.Item>
            <CheckboxAddon>
              <Checkbox
                id="state-bulk-update-select-all-states-checkbox"
                checked={selectAllStates}
                disabled={!allStateIds.length}
                onChange={(e) => statesSelectAll.onToggle(e.target.checked)}
              >
                Select All
              </Checkbox>
            </CheckboxAddon>
          </SelectWithAddon>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <SelectWithAddon>
            <Form.Item
              name='categories'
              label='Categories:'
              rules={[{ required: true, message: 'Please select at least one category' }]}
            >
              <Select
                id="state-bulk-update-categories-select"
                mode='multiple'
                showSearch
                allowClear
                filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                options={categoriesOptions}
                placeholder='Enter Category Name'
                onChange={categoriesSelectAll.onChange}
              />
            </Form.Item>
            <CheckboxAddon>
              <Checkbox
                id="state-bulk-update-select-all-categories-checkbox"
                checked={selectAllCategories}
                disabled={!allCategoryIds.length}
                onChange={(e) => categoriesSelectAll.onToggle(e.target.checked)}
              >
                Select All
              </Checkbox>
            </CheckboxAddon>
          </SelectWithAddon>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item name='pharmacy' label='Order Type Pharmacy:' rules={[{ required: true, message: 'Please select a pharmacy' }]}>
            <AutoComplete
              id="state-bulk-update-pharmacy-autocomplete"
              loading={pharmaciesLoading}
              defaultActiveFirstOption
              onSearch={searchPharmacies}
              onSelect={(_, option) => selectPharmacyByNcpdpId(option?.ncpdpId)}
              options={pharmacyOptions}
              placeholder='Enter Pharmacy Name'
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item
            name='consultationType'
            label='Initial Consultation Type'
            rules={[{ required: true, message: 'Please select initial consultation type' }]}
          >
            <Select id="state-bulk-update-initial-consultation-select" placeholder='Select Initial Consultation Type'>
              {CONSULTATION_TYPES.map(type => <Select.Option key={type.key} value={type.value}>{type.label}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item
            name='renewalConsultationType'
            label='Renewal Consultation Type'
            rules={[{ required: true, message: 'Please select renewal consultation type' }]}
          >
            <Select id="state-bulk-update-renewal-consultation-select" placeholder='Select Renewal Consultation Type'>
              {CONSULTATION_TYPES.map(type => <Select.Option key={type.key} value={type.value}>{type.label}</Select.Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} align='middle' justify='space-between'>
        <Col>
          <SwitchLabelRow>
            <label htmlFor="state-bulk-update-should-update-cases-switch">Update Existing Cases</label>
            <InlineSwitchItem name='shouldUpdateCases' valuePropName='checked'>
              <Switch
                id="state-bulk-update-should-update-cases-switch"
                disabled={previewLoading}
                onChange={async (checked) => {
                  if (!checked) return;

                  const { states: selectedStates, categories: selectedCategories } = form.getFieldsValue(['states', 'categories']);
                  if (!selectedStates?.length || !selectedCategories?.length) {
                    message.warning('Please select states and categories first');
                    form.setFieldsValue({ shouldUpdateCases: false });
                    return;
                  }

                  setPreviewLoading(true);
                  try {
                    const { data } = await previewBulkUpdateCases(selectedStates, selectedCategories);
                    Modal.confirm({
                      title: 'Update Existing Cases',
                      content: `This will immediately update the pharmacy on ${data.count} open case(s). Proceed?`,
                      onCancel: () => form.setFieldsValue({ shouldUpdateCases: false })
                    });
                  } catch {
                    message.error('Failed to fetch affected case count');
                    form.setFieldsValue({ shouldUpdateCases: false });
                  } finally {
                    setPreviewLoading(false);
                  }
                }}
              />
            </InlineSwitchItem>
          </SwitchLabelRow>
        </Col>
        <Col>
          <Button id="state-bulk-update-submit-btn" type='primary' htmlType='submit'>Update</Button>
        </Col>
      </Row>
    </BulkUpdateForm>
  );
};
